/**
 * Header / menu alerts for pending workspace invitations.
 *
 * The user's name and the "Workspace Controls" menu entry show a red dot while
 * they have invitations they haven't looked at yet. "Looked at" = landed on
 * Workspace Controls (where the invites are listed at the top). We track the
 * set of seen invitation IDs in localStorage so the dot stays cleared across
 * navigations and only re-appears for genuinely new invitations.
 *
 * localStorage isn't reactive on its own, so `markInvitationsSeen` dispatches a
 * window event the count hook listens to — that way clearing the dot on the
 * Workspace Controls page updates the header badge live.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from './useAuth';
import { useMyVaultInvitations } from './useWorkspaceTeam';

const SEEN_KEY = 'fias.seen-invitation-ids';
const SEEN_EVENT = 'fias:invitations-seen';

function readSeen(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]');
    return Array.isArray(raw) ? new Set(raw as string[]) : new Set();
  } catch {
    return new Set();
  }
}

/** Mark the given invitation IDs as seen and notify any mounted count hooks. */
export function markInvitationsSeen(ids: string[]): void {
  if (typeof window === 'undefined' || ids.length === 0) return;
  const seen = readSeen();
  let changed = false;
  for (const id of ids) {
    if (!seen.has(id)) {
      seen.add(id);
      changed = true;
    }
  }
  if (!changed) return;
  localStorage.setItem(SEEN_KEY, JSON.stringify([...seen]));
  window.dispatchEvent(new CustomEvent(SEEN_EVENT));
}

/**
 * Number of pending invitations the user hasn't seen yet — drives the header /
 * menu alert dots. Reactive: refetches with the invitations query and re-reads
 * the seen set whenever `markInvitationsSeen` fires.
 */
export function useUnseenInvitationCount(): number {
  const { isAuthenticated } = useAuth();
  const { data: invitations } = useMyVaultInvitations({ enabled: isAuthenticated });
  const [seenTick, setSeenTick] = useState(0);

  useEffect(() => {
    const onSeen = () => setSeenTick((t) => t + 1);
    window.addEventListener(SEEN_EVENT, onSeen);
    return () => window.removeEventListener(SEEN_EVENT, onSeen);
  }, []);

  // seenTick is an intentional extra dependency: it forces a re-read of the
  // localStorage seen-set after markInvitationsSeen fires.
  return useMemo(() => {
    const seen = readSeen();
    return (invitations ?? []).filter((i) => !seen.has(i.invitationId)).length;
  }, [invitations, seenTick]);
}
